import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Alert,
  RefreshControl,
  SafeAreaView,
} from 'react-native';
import { bankAccountService, PayoutInfo, PayoutHistory, BankAccount } from '../services/bankAccountService';
import { AuthManager } from '../utils/authManager';

interface PayoutDashboardScreenProps {
  onBack: () => void;
}

const PayoutDashboardScreen: React.FC<PayoutDashboardScreenProps> = ({ onBack }) => {
  const [merchantId, setMerchantId] = useState<string>('');
  const [payoutInfo, setPayoutInfo] = useState<PayoutInfo | null>(null);
  const [payoutHistory, setPayoutHistory] = useState<PayoutHistory[]>([]);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadMerchantId();
  }, []);

  const loadMerchantId = async () => {
    try {
      const businessOwner = await AuthManager.getSavedBusinessOwner();
      if (businessOwner) {
        setMerchantId(businessOwner.id);
        loadPayoutData(businessOwner.id);
      }
    } catch (error) {
      console.error('Error loading merchant ID:', error);
    }
  };

  const loadPayoutData = async (id: string) => {
    try {
      setIsLoading(true);
      const [payoutInfoData, historyData, accountsData] = await Promise.all([
        bankAccountService.getPayoutInfo(id),
        bankAccountService.getPayoutHistory(id),
        bankAccountService.getBankAccounts(id)
      ]);

      setPayoutInfo(payoutInfoData);
      setPayoutHistory(historyData);
      setBankAccounts(accountsData);
    } catch (error) {
      console.error('Error loading payout data:', error);
      Alert.alert('Error', 'Failed to load payout information');
    } finally {
      setIsLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    if (merchantId) {
      await loadPayoutData(merchantId);
    }
    setRefreshing(false);
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'paid': return '#34C759';
      case 'pending': return '#FF9500';
      case 'failed': return '#FF3B30';
      default: return '#8E8E93';
    }
  };

  const getStatusIcon = (status: string): string => {
    switch (status) {
      case 'paid': return '✅';
      case 'pending': return '⏳';
      case 'failed': return '❌';
      default: return '❓';
    }
  };

  const handlePayoutScheduleChange = async (schedule: 'daily' | 'weekly' | 'monthly') => {
    if (!merchantId) return;

    try {
      const success = await bankAccountService.updatePayoutSchedule(merchantId, schedule);
      if (success) {
        Alert.alert('Success', 'Payout schedule updated successfully');
        loadPayoutData(merchantId);
      } else {
        Alert.alert('Error', 'Failed to update payout schedule');
      }
    } catch (error) {
      console.error('Error updating payout schedule:', error);
      Alert.alert('Error', 'An error occurred while updating payout schedule');
    }
  };

  const renderPayoutSummary = () => {
    if (!payoutInfo) return null;

    return (
      <View style={styles.summaryCard}>
        <Text style={styles.summaryTitle}>Payout Summary</Text>
        
        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>Next Payout</Text>
          <Text style={styles.summaryValue}>
            {formatCurrency(payoutInfo.nextPayoutAmount)}
          </Text>
        </View>

        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>Scheduled Date</Text>
          <Text style={styles.summaryValue}>
            {formatDate(payoutInfo.nextPayoutDate)}
          </Text>
        </View>

        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>Pending Amount</Text>
          <Text style={styles.summaryValue}>
            {formatCurrency(payoutInfo.pendingAmount)}
          </Text>
        </View>

        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>Total Earnings</Text>
          <Text style={[styles.summaryValue, styles.totalEarnings]}>
            {formatCurrency(payoutInfo.totalEarnings)}
          </Text>
        </View>
      </View>
    );
  };

  const renderBankAccountInfo = () => {
    if (!payoutInfo || bankAccounts.length === 0) return null;

    const defaultAccount = bankAccounts.find(account => account.isDefault) || bankAccounts[0];

    return (
      <View style={styles.bankCard}>
        <Text style={styles.bankTitle}>Bank Account</Text>
        
        <View style={styles.bankInfo}>
          <Text style={styles.bankName}>{payoutInfo.bankAccount.bankName}</Text>
          <Text style={styles.bankDetails}>
            {payoutInfo.bankAccount.accountType.charAt(0).toUpperCase() + payoutInfo.bankAccount.accountType.slice(1)} ••••{payoutInfo.bankAccount.last4}
          </Text>
        </View>

        <View style={styles.verificationStatus}>
          <Text style={styles.verificationText}>
            {defaultAccount.isVerified ? '✅ Verified' : '⚠️ Pending Verification'}
          </Text>
        </View>
      </View>
    );
  };

  const renderPayoutSchedule = () => {
    if (!payoutInfo) return null;

    const schedules = [
      { key: 'daily', label: 'Daily', description: 'Every business day' },
      { key: 'weekly', label: 'Weekly', description: 'Every Friday' },
      { key: 'monthly', label: 'Monthly', description: 'First business day' },
    ];

    return (
      <View style={styles.scheduleCard}>
        <Text style={styles.scheduleTitle}>Payout Schedule</Text>
        
        {schedules.map((schedule) => (
          <TouchableOpacity
            key={schedule.key}
            style={[
              styles.scheduleOption,
              payoutInfo.payoutSchedule === schedule.key && styles.scheduleOptionSelected
            ]}
            onPress={() => handlePayoutScheduleChange(schedule.key as any)}
          >
            <View style={styles.scheduleInfo}>
              <Text style={[
                styles.scheduleLabel,
                payoutInfo.payoutSchedule === schedule.key && styles.scheduleLabelSelected
              ]}>
                {schedule.label}
              </Text>
              <Text style={styles.scheduleDescription}>{schedule.description}</Text>
            </View>
            {payoutInfo.payoutSchedule === schedule.key && (
              <Text style={styles.scheduleCheck}>✓</Text>
            )}
          </TouchableOpacity>
        ))}
      </View>
    );
  };

  const renderPayoutHistory = () => {
    if (payoutHistory.length === 0) {
      return (
        <View style={styles.emptyState}>
          <Text style={styles.emptyStateTitle}>No Payouts Yet</Text>
          <Text style={styles.emptyStateText}>
            Your payout history will appear here once you start receiving payments from customer bookings.
          </Text>
        </View>
      );
    }

    return (
      <View style={styles.historyCard}>
        <Text style={styles.historyTitle}>Recent Payouts</Text>
        
        {payoutHistory.slice(0, 5).map((payout) => (
          <View key={payout.id} style={styles.historyItem}>
            <View style={styles.historyInfo}>
              <Text style={styles.historyAmount}>
                {formatCurrency(payout.amount)}
              </Text>
              <Text style={styles.historyDate}>
                {payout.paidDate ? formatDate(payout.paidDate) : formatDate(payout.scheduledDate)}
              </Text>
            </View>
            
            <View style={styles.historyStatus}>
              <Text style={styles.historyStatusIcon}>
                {getStatusIcon(payout.status)}
              </Text>
              <Text style={[
                styles.historyStatusText,
                { color: getStatusColor(payout.status) }
              ]}>
                {payout.status.charAt(0).toUpperCase() + payout.status.slice(1)}
              </Text>
            </View>
          </View>
        ))}

        {payoutHistory.length > 5 && (
          <TouchableOpacity style={styles.viewAllButton}>
            <Text style={styles.viewAllText}>View All Payouts</Text>
          </TouchableOpacity>
        )}
      </View>
    );
  };

  if (isLoading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity onPress={onBack} style={styles.backButton}>
            <Text style={styles.backButtonText}>← Back</Text>
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Payout Dashboard</Text>
          <View style={styles.headerSpacer} />
        </View>
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>Loading payout information...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={onBack} style={styles.backButton}>
          <Text style={styles.backButtonText}>← Back</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Payout Dashboard</Text>
        <View style={styles.headerSpacer} />
      </View>

      {/* Content */}
      <ScrollView
        style={styles.content}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {renderPayoutSummary()}
        {renderBankAccountInfo()}
        {renderPayoutSchedule()}
        {renderPayoutHistory()}
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 15,
    backgroundColor: 'white',
    borderBottomWidth: 1,
    borderBottomColor: '#E1E5E9',
  },
  backButton: {
    marginRight: 15,
  },
  backButtonText: {
    fontSize: 16,
    color: '#007AFF',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1C1C1E',
    flex: 1,
  },
  headerSpacer: {
    width: 60,
  },
  content: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    fontSize: 16,
    color: '#8E8E93',
  },
  summaryCard: {
    margin: 20,
    padding: 20,
    backgroundColor: 'white',
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  summaryTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 16,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  summaryLabel: {
    fontSize: 16,
    color: '#8E8E93',
  },
  summaryValue: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1C1C1E',
  },
  totalEarnings: {
    fontSize: 18,
    color: '#34C759',
  },
  bankCard: {
    marginHorizontal: 20,
    marginBottom: 20,
    padding: 20,
    backgroundColor: 'white',
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  bankTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 12,
  },
  bankInfo: {
    marginBottom: 12,
  },
  bankName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1C1C1E',
    marginBottom: 4,
  },
  bankDetails: {
    fontSize: 14,
    color: '#8E8E93',
  },
  verificationStatus: {
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#E1E5E9',
  },
  verificationText: {
    fontSize: 14,
    color: '#34C759',
    fontWeight: '500',
  },
  scheduleCard: {
    marginHorizontal: 20,
    marginBottom: 20,
    padding: 20,
    backgroundColor: 'white',
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  scheduleTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 16,
  },
  scheduleOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E1E5E9',
    marginBottom: 8,
  },
  scheduleOptionSelected: {
    borderColor: '#007AFF',
    backgroundColor: '#F0F8FF',
  },
  scheduleInfo: {
    flex: 1,
  },
  scheduleLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1C1C1E',
    marginBottom: 2,
  },
  scheduleLabelSelected: {
    color: '#007AFF',
  },
  scheduleDescription: {
    fontSize: 14,
    color: '#8E8E93',
  },
  scheduleCheck: {
    fontSize: 18,
    color: '#007AFF',
    fontWeight: 'bold',
  },
  historyCard: {
    marginHorizontal: 20,
    marginBottom: 20,
    padding: 20,
    backgroundColor: 'white',
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  historyTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 16,
  },
  historyItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  historyInfo: {
    flex: 1,
  },
  historyAmount: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1C1C1E',
    marginBottom: 2,
  },
  historyDate: {
    fontSize: 14,
    color: '#8E8E93',
  },
  historyStatus: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  historyStatusIcon: {
    fontSize: 16,
    marginRight: 6,
  },
  historyStatusText: {
    fontSize: 14,
    fontWeight: '500',
  },
  viewAllButton: {
    marginTop: 12,
    paddingVertical: 8,
    alignItems: 'center',
  },
  viewAllText: {
    fontSize: 16,
    color: '#007AFF',
    fontWeight: '500',
  },
  emptyState: {
    margin: 20,
    padding: 40,
    backgroundColor: 'white',
    borderRadius: 12,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  emptyStateTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 8,
  },
  emptyStateText: {
    fontSize: 14,
    color: '#8E8E93',
    textAlign: 'center',
    lineHeight: 20,
  },
});

export default PayoutDashboardScreen;



